import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Warning } from "@phosphor-icons/react";

/**
 * Reusable Radix-based confirmation dialog. Replaces window.confirm().
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <ConfirmDialog
 *     open={open} onOpenChange={setOpen}
 *     title="Supprimer l'article ?"
 *     description="Cette action déplace l'article dans la corbeille pendant 30 jours."
 *     confirmLabel="Supprimer"
 *     danger
 *     onConfirm={handleDelete}
 *   />
 */
export function ConfirmDialog({
  open, onOpenChange,
  title = "Confirmer",
  description,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  danger = false,
  onConfirm,
  testIdPrefix = "confirm",
}) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try { await onConfirm?.(); } finally { setBusy(false); onOpenChange?.(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {danger && <Warning size={20} weight="duotone" className="text-destructive"/>}
            {title}
          </DialogTitle>
        </DialogHeader>
        {description && (
          <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        )}
        <DialogFooter className="mt-4">
          <Button variant="secondary" onClick={() => onOpenChange?.(false)} disabled={busy}
            data-testid={`${testIdPrefix}-cancel`}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? "destructive" : "default"} onClick={handle} disabled={busy}
            data-testid={`${testIdPrefix}-ok`}>
            {busy ? "…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmDialog;
